var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import { useForm, Controller, FormProvider, } from 'react-hook-form';
import { Button, TextField, SelectionControlLabel, Checkbox, LoadingButton, DropdownField, MenuItem, Typography, } from '@one-thd/sui-atomic-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { setOverlayEvent, track } from '../../../utils/analytics';
import { INVALID_PAYMENT_ERROR_CODES } from '../../../constants/error-codes';
import { Alert } from '../../shared/alert/alert';
import { ERR_GENERIC, ERR_INVALID_PAYMENT, ERR_UNABLE_TO_SAVE_PAYMENT, } from '../../../constants/response-error-messages';
import { useIsMounted } from '../../../hooks/use-is-mounted';
import { CHANGES_SAVED_MSG } from '../../../constants/response-success-messages';
import { DefaultCreditCardModal } from '../../shared/default-credit-card-modal/default-credit-card-modal';
import { b2cPaymentsApi } from '../../../api/b2c/payments';
import { mapB2BEditCreditCardToRequestModel, mapB2CEditCreditCardToRequestModel, } from '../../../utils/mapping-payments';
import { Spinner } from '../../shared/spinner/spinner';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { CreditCardBrands } from '../../../constants/credit-card';
import { ZipCodeCitySelectFields } from '../../shared/zipcode/zipcode';
import { getCreditCardFormValues, getTextFieldStatus, isAllowedCharacter, } from '../../../utils/form';
import { usePayments } from '../../../hooks/use-payments';
import { FormTitle } from '../../shared/form-title/form-title';
import { expirationMonthValues, expirationYearValues, getPaymentsErrorCode, } from '../../../utils/payments';
import { TAlertStatus, } from '../../../models/common/alert-messages';
import { useModal } from '../../../hooks/use-modal';
import { useAddresses } from '../../../hooks/use-addresses';
import { useDrawer } from '../../../hooks/use-drawer';
import { CardType } from '../../../constants/payments';
import { b2bValidationSchema, b2cValidationSchema } from './validation';
import { setEditCardAnalytics, setSuccessSetAsDefaultCardAnalytics, } from '../../../utils/payments-analytics';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { TextButton } from '../../shared/text-button/text-button';
import { AddressDropdownSelector } from '../../shared/address-select/address-dropdown';
import { SelectBillingAddress } from '../../shared/select-billing-address/select-billing-address';
import { AddBillingAddress } from '../../shared/add-billing-address/add-billing-address';
import { ERROR_CODES } from '../../../constants/custom-errors';
import { CustomerRole } from '../../../models/common/customer';
export const EditCreditCardForm = ({ card, onCancelForm, onEditSuccess, fromRemotePurchases, }) => {
    const [loading, setLoading] = useState(false);
    const [initialAddressLoading, setInitialAddressLoading] = useState(false);
    const [billingAddress, setBillingAddress] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const { isB2B, creditCards, setAlertMessage } = usePayments();
    const { openModal, hideModal, setComponentProps } = useModal();
    const { getAddresses } = useAddresses();
    const { openDrawer, hideDrawer } = useDrawer();
    const isMountedRef = useIsMounted();
    const { role } = useCustomerInfo();
    const isPurchaser = role === CustomerRole.PURCHASER;
    const addressId = card === null || card === void 0 ? void 0 : card.addressId;
    const defaultValues = getCreditCardFormValues(card);
    const citiAccountId = card === null || card === void 0 ? void 0 : card.citiAccountId;
    const paymentId = card === null || card === void 0 ? void 0 : card.paymentId;
    const isPropCardType = (card === null || card === void 0 ? void 0 : card.cardBrand) === CreditCardBrands.PROP;
    const validationSchema = isB2B ? b2bValidationSchema : b2cValidationSchema;
    const formMethods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues,
        mode: 'onTouched',
        context: { isB2B },
    });
    const { control, formState: { errors: formErrors, isValid, dirtyFields }, setValue, getValues, handleSubmit, trigger: triggerFormValidation, } = formMethods;
    useEffect(() => {
        if (addressId && isB2B) {
            setLoading(true);
            setInitialAddressLoading(true);
            b2bPaymentsApi
                .getBillingAddress(`${addressId}`, isPurchaser)
                .then(({ data }) => {
                const { billingAddress: address } = data;
                setBillingAddressValues(address);
                setBillingAddress(address);
                triggerFormValidation();
            })
                .catch((error) => {
                var _a, _b, _c, _d;
                const errorCode = (_d = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.addresses) === null || _c === void 0 ? void 0 : _c.errors[0]) === null || _d === void 0 ? void 0 : _d.errorCode;
                setErrorMsg(ERR_GENERIC);
                track('credit-card.actions.post-page-error', {
                    type: 'error',
                    displayType: 'inline',
                    pageSection: 'edit credit card',
                    message: ERR_GENERIC.message,
                    code: errorCode,
                });
            })
                .finally(() => {
                setLoading(false);
                setInitialAddressLoading(false);
            });
        }
        if (!isB2B) {
            getAddresses({ page: 0, pageSize: 0 }).catch((error) => {
                if (error.message === ERROR_CODES.NO_SAVED_ADR_CODE) {
                    return;
                }
                throw error;
            });
        }
        if (!addressId)
            triggerFormValidation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { isZipCodePending, isZipCodeApiError, isHDCard } = getValues();
    const isFormDirty = () => Object.values(dirtyFields).some((value) => {
        return value || false;
    });
    const handleOnKeyPress = (event, field) => !isAllowedCharacter(event.key, field) && event.preventDefault();
    const setBillingAddressValues = (address) => {
        const cityState = `${address.cityName}, ${address.stateCode}`;
        setValue('addressLine1', address.addressLine1 || '');
        setValue('addressLine2', address.addressLine2 || '');
        setValue('cityState', cityState);
        setValue('zipCode', address.postalCode || '');
    };
    const setSuccessMsg = () => {
        if (card) {
            setAlertMessage({
                paymentId: card.paymentId,
                alertStatus: TAlertStatus.SUCCESS,
                text: CHANGES_SAVED_MSG.message,
                dataTestId: CHANGES_SAVED_MSG.dataTestId,
            });
        }
    };
    const onSuccessHandler = (isDefault) => {
        onEditSuccess();
        setSuccessMsg();
        setEditCardAnalytics('credit card', isDefault, card.cardBrand, card.cardType);
        if (!defaultValues.isDefault && isDefault) {
            setSuccessSetAsDefaultCardAnalytics();
        }
    };
    const onErrorHandler = (err) => {
        const errorCode = getPaymentsErrorCode(err) || '';
        if (INVALID_PAYMENT_ERROR_CODES[errorCode]) {
            setErrorMsg(ERR_INVALID_PAYMENT);
            track('credit-card.actions.post-page-error', {
                type: 'error',
                displayType: 'inline',
                pageSection: 'edit credit card',
                message: ERR_INVALID_PAYMENT.message,
                code: errorCode,
            });
            return;
        }
        setErrorMsg(ERR_UNABLE_TO_SAVE_PAYMENT);
        track('credit-card.actions.post-page-error', {
            type: 'error',
            displayType: 'inline',
            pageSection: 'edit credit card',
            message: ERR_UNABLE_TO_SAVE_PAYMENT.message,
            code: errorCode || 'n/a',
        });
    };
    const executeUpdateCreditCard = (creditCardRequest) => {
        const { isDefault } = creditCardRequest;
        if (isB2B) {
            return b2bPaymentsApi
                .updatePayment(creditCardRequest, CardType.CREDIT_CARD)
                .then(() => {
                onSuccessHandler(isDefault);
            })
                .catch(onErrorHandler);
        }
        return b2cPaymentsApi
            .updateCreditCard(creditCardRequest)
            .then(() => {
            onSuccessHandler(isDefault);
        })
            .catch(onErrorHandler);
    };
    const onSubmitForm = (values) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isFormDirty()) {
            onCancelForm();
            return;
        }
        const isFormValid = !isZipCodePending || !isZipCodeApiError || isValid;
        if (isFormValid) {
            const creditCardDataFromResponse = (creditCards === null || creditCards === void 0 ? void 0 : creditCards.response).find((newCard) => newCard.paymentId === paymentId);
            if (creditCardDataFromResponse) {
                const creditCardRequest = isB2B
                    ? mapB2BEditCreditCardToRequestModel(values, billingAddress, creditCardDataFromResponse)
                    : mapB2CEditCreditCardToRequestModel(values, creditCardDataFromResponse);
                if (values.isDefault && !defaultValues.isDefault) {
                    openModal(DefaultCreditCardModal, {
                        card,
                        onConfirmDefault: () => {
                            setComponentProps({ isLoading: true });
                            executeUpdateCreditCard(creditCardRequest).finally(() => {
                                hideModal();
                            });
                        },
                        isLoading: false,
                    });
                    setOverlayEvent('confirm your new default payment method');
                }
                else {
                    setLoading(true);
                    executeUpdateCreditCard(creditCardRequest).finally(() => {
                        if (isMountedRef.current)
                            setLoading(false);
                    });
                }
            }
        }
    });
    const onInvalid = (values) => __awaiter(void 0, void 0, void 0, function* () {
        // Convert messages to a Set to ensure uniqueness
        const uniqueMessages = new Set(Object.values(values).map((entry) => entry.message));
        const combinedMessages = Array.from(uniqueMessages).join(' ');
        track('credit-card.actions.post-page-error', {
            type: 'advisory',
            displayType: 'inline',
            pageSection: 'edit credit card',
            message: combinedMessages,
        });
    });
    const onViewAll = () => {
        openDrawer(SelectBillingAddress, {
            hideDrawer,
            onSave: (newAddrId) => {
                setValue('billingAddressID', newAddrId, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            },
            getValues,
            onAddBilling,
        });
    };
    const onAddBilling = () => {
        openDrawer(AddBillingAddress, {
            hideDrawer,
            onAdd: (newAddrId) => {
                setValue('billingAddressID', newAddrId, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            },
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Spinner, { active: initialAddressLoading }),
        React.createElement("div", { className: "lg:sui-max-w-[437px] sui-flex sui-flex-col sui-gap-6" },
            React.createElement(FormTitle, { title: "EDIT CREDIT CARD", actionLabel: "Cancel", onActionClick: onCancelForm }),
            fromRemotePurchases && (React.createElement(Typography, { variant: "body-base" }, "Additional card details are required to authorize it for remote purchases.")),
            React.createElement(FormProvider, Object.assign({}, formMethods),
                React.createElement("form", { onSubmit: handleSubmit(onSubmitForm, onInvalid) },
                    React.createElement("div", { className: "sui-grid sui-gap-y-6 sui-mb-10" },
                        React.createElement(Controller, { name: "cardNickName", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                return (React.createElement(TextField, { label: "Card Nickname", status: getTextFieldStatus(formErrors.cardNickName), statusMessage: (_a = formErrors.cardNickName) === null || _a === void 0 ? void 0 : _a.message, optional: true, value: value || '', name: name, onChange: onChange, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-testid': 'nicknameField', maxLength: 100, 'data-qm-mask': '' }),
                                    } }));
                            } }),
                        React.createElement(Controller, { name: "cardNumber", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                return (React.createElement(TextField, { label: "Card Number*", disabled: true, statusMessage: (_a = formErrors.cardNumber) === null || _a === void 0 ? void 0 : _a.message, value: value, name: name, onChange: onChange, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '' }),
                                    } }));
                            } }),
                        React.createElement(Controller, { name: "cardHolderName", control: control, render: ({ field }) => {
                                var _a;
                                const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                return (React.createElement(TextField, { disabled: !!citiAccountId, label: isPropCardType ? 'Name on Card' : 'Name on Card*', status: getTextFieldStatus(formErrors.cardHolderName), statusMessage: (_a = formErrors.cardHolderName) === null || _a === void 0 ? void 0 : _a.message, value: value, name: name, onChange: onChange, InputProps: {
                                        inputProps: Object.assign(Object.assign({}, rest), { 'data-qm-mask': '' }),
                                    }, optional: isPropCardType }));
                            } }),
                        !isHDCard && (React.createElement("div", { className: "sui-grid sui-grid-cols-2 sui-gap-x-6" },
                            React.createElement("div", null,
                                React.createElement(Controller, { name: "cardExpiryMonth", control: control, render: ({ field }) => {
                                        var _a;
                                        const { ref, onChange, value: fieldValue } = field, rest = __rest(field, ["ref", "onChange", "value"]);
                                        return (React.createElement(DropdownField, { fullWidth: true, label: "Expiration Month*", value: fieldValue, id: "cardExpiryMonth", status: getTextFieldStatus(formErrors.cardExpiryMonth), statusMessage: (_a = formErrors.cardExpiryMonth) === null || _a === void 0 ? void 0 : _a.message, dropdownProps: Object.assign({ onChange: (event) => __awaiter(void 0, void 0, void 0, function* () {
                                                    onChange(event);
                                                    yield formMethods.trigger(['cardExpiryYear']);
                                                }) }, rest) }, expirationMonthValues.map(({ value: itemValue, label }) => (React.createElement(MenuItem, { key: itemValue, value: itemValue }, label)))));
                                    } })),
                            React.createElement("div", null,
                                React.createElement(Controller, { name: "cardExpiryYear", control: control, render: ({ field }) => {
                                        var _a;
                                        const { ref, onChange, value: fieldValue } = field, rest = __rest(field, ["ref", "onChange", "value"]);
                                        return (React.createElement(DropdownField, { fullWidth: true, label: "Expiration Year*", value: fieldValue, id: "cardExpiryYear", status: getTextFieldStatus(formErrors.cardExpiryYear), statusMessage: (_a = formErrors.cardExpiryYear) === null || _a === void 0 ? void 0 : _a.message, dropdownProps: Object.assign({ onChange: (event) => __awaiter(void 0, void 0, void 0, function* () {
                                                    onChange(event);
                                                    yield formMethods.trigger(['cardExpiryMonth']);
                                                }) }, rest) }, expirationYearValues.map(({ value, label }) => (React.createElement(MenuItem, { key: value, value: value }, label)))));
                                    } })))),
                        isB2B ? (React.createElement(React.Fragment, null,
                            React.createElement(Controller, { name: "addressLine1", control: control, render: ({ field }) => {
                                    var _a;
                                    const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                    return (React.createElement(TextField, { label: "Billing Address*", status: getTextFieldStatus(formErrors.addressLine1), statusMessage: (_a = formErrors.addressLine1) === null || _a === void 0 ? void 0 : _a.message, value: value, name: name, onChange: onChange, InputProps: {
                                            inputProps: Object.assign(Object.assign({}, rest), { onKeyPress: (event) => {
                                                    handleOnKeyPress(event, 'addressLine1');
                                                }, maxLength: 60, 'data-qm-mask': '' }),
                                        } }));
                                } }),
                            React.createElement(Controller, { name: "addressLine2", control: control, render: ({ field }) => {
                                    var _a;
                                    const { ref, value, name, onChange } = field, rest = __rest(field, ["ref", "value", "name", "onChange"]);
                                    return (React.createElement(TextField, { label: "Billing Address Line 2", status: getTextFieldStatus(formErrors.addressLine2), statusMessage: (_a = formErrors.addressLine2) === null || _a === void 0 ? void 0 : _a.message, optional: true, value: value || '', name: name, onChange: onChange, InputProps: {
                                            inputProps: Object.assign(Object.assign({}, rest), { onKeyPress: (event) => {
                                                    handleOnKeyPress(event, 'addressLine2');
                                                }, maxLength: 60, 'data-qm-mask': '' }),
                                        } }));
                                } }),
                            !initialAddressLoading && React.createElement(ZipCodeCitySelectFields, null))) : (React.createElement(React.Fragment, null,
                            React.createElement(Controller, { name: "billingAddressID", control: control, defaultValue: null, render: ({ field }) => {
                                    var _a;
                                    const { value: fieldValue, onChange, ref } = field, rest = __rest(field, ["value", "onChange", "ref"]);
                                    return (React.createElement(AddressDropdownSelector, Object.assign({ status: getTextFieldStatus(formErrors.billingAddressID), statusMessage: (_a = formErrors.billingAddressID) === null || _a === void 0 ? void 0 : _a.message, selectedAddrID: fieldValue, onChange: (addrId) => {
                                            onChange(addrId);
                                        } }, rest)));
                                } }),
                            React.createElement("div", { className: "sui-grid sui-grid-cols-2 sui-gap-32 sm:sui-gap-x-32" },
                                onViewAll && (React.createElement(TextButton, { className: "sui-text-left", onClick: onViewAll }, "View all addresses")),
                                onAddBilling && (React.createElement(TextButton, { className: "sui-text-right", onClick: onAddBilling, dataTestId: "add-address-btn" }, "+ Add Address"))))),
                        React.createElement("div", { className: "sui-my-2" },
                            React.createElement(SelectionControlLabel, { label: "Make this my default payment method" },
                                React.createElement(Controller, { name: "isDefault", control: control, render: (_a) => {
                                        var _b;
                                        var _c = _a.field, { ref, value } = _c, rest = __rest(_c, ["ref", "value"]);
                                        return (React.createElement(Checkbox, Object.assign({}, rest, { checked: value, disabled: !!(card === null || card === void 0 ? void 0 : card.isDefault) ||
                                                (isB2B && !((_b = card === null || card === void 0 ? void 0 : card.actions) === null || _b === void 0 ? void 0 : _b.setasDefault)) })));
                                    } }))),
                        errorMsg && React.createElement(Alert, { status: "error" }, errorMsg.message),
                        React.createElement("div", { className: "sui-grid sui-grid-cols-1 sui-gap-5 sm:sui-grid-cols-2" },
                            React.createElement("div", { className: "sui-order-last sm:sui-order-first" },
                                React.createElement(Button, { fullWidth: true, variant: "secondary", onClick: onCancelForm }, "Cancel")),
                            React.createElement(LoadingButton, { loading: loading && !initialAddressLoading, fullWidth: true, variant: "primary", type: "submit", "data-testid": "submitBtn" }, "Save Changes"))))))));
};
